import { Suspense, useMemo } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { ApolloProvider } from '@apollo/client'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import { SnackbarProvider } from 'notistack'
import { theme, getDataClient, env } from 'config'
import modules from 'modules'
import {
  AuthProvider,
  Layout,
  ConfirmationContextProvider,
  RemoteConfigProvider,
} from 'shared/components'
import { PasswordPolicyWarningProvider } from 'shared/components/PasswordPolicyContext'
import { TwoFactorPromptProvider } from 'shared/components/TwoFactorPromptContext'
import { useCurrentUser } from 'shared/hooks'
import useLocale from 'shared/hooks/use-locale'
import AuthLoader from 'shared/components/AuthLoader'
import StartupPageLoader from 'shared/components/StartupPageLoader'
import { GoogleOAuthProvider } from 'react-oauth-google'

const App = () => {
  const user = useCurrentUser()
  const locale = useLocale()
  const dataClient = useMemo(
    () => getDataClient(user.auth),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user.auth, user.user?.uid]
  )

  return (
    <AuthProvider value={user}>
      <RemoteConfigProvider auth={user.auth}>
        <ApolloProvider client={dataClient}>
          <SnackbarProvider>
            <ThemeProvider theme={theme}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                locale={locale}
              >
                <CssBaseline />
                <PasswordPolicyWarningProvider>
                  <TwoFactorPromptProvider>
                    <AuthLoader user={user}>
                      <Suspense fallback={<StartupPageLoader />}>
                        <GoogleOAuthProvider
                          clientId={env.googleClientId}
                          onScriptLoadError={onScriptLoadError}
                          onScriptLoadSuccess={onScriptLoadSuccess}
                        >
                          <ConfirmationContextProvider>
                            <BrowserRouter>
                              <Layout routes={modules} />
                            </BrowserRouter>
                          </ConfirmationContextProvider>
                        </GoogleOAuthProvider>
                      </Suspense>
                    </AuthLoader>
                  </TwoFactorPromptProvider>
                </PasswordPolicyWarningProvider>
              </LocalizationProvider>
            </ThemeProvider>
          </SnackbarProvider>
        </ApolloProvider>
      </RemoteConfigProvider>
    </AuthProvider>
  )
}

export default App

const onScriptLoadError = () => {
  console.error('onScriptLoadError')
}

const onScriptLoadSuccess = () => {
  console.log('onScriptLoadSuccess')
}
